/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import {
  applyMockHostOverride,
  applyVersionOverride,
  closeAppVersionOverride,
  AppVersionOverrideFocus,
  focusNextControl,
  focusPrevControl,
  resetMockHostOverride,
  resetVersionOverride,
  selectAppVersionOverrideFocusedIndex
} from "../../../viewModel/appVersionOverride";
import { InputAction, ModalController } from "../../../viewModel/navigation";
import type { UnityAppDispatch, UnityRootState } from "../../../viewModel";

/** Modal name constant for the version override overlay. */
export const APP_VERSION_OVERLAY_MODAL_NAME: string = "APP_VERSION_OVERLAY";

/**
 * ModalController for the debug-only override overlay.
 *
 * D-pad routing:
 *   NAV_DOWN / NAV_RIGHT → move focus forward (version field → its Apply →
 *                          its Reset → mock field → its Apply → its Reset)
 *   NAV_UP   / NAV_LEFT  → move focus backward
 *   NAV_OK               → activate the focused control
 *   BACK                 → close the overlay, changing nothing
 *
 * Both axes move the same single linear list. The layout is two stacked
 * sections, each a field above its own Apply/Reset row, so DOWN walks the
 * whole overlay top to bottom and LEFT/RIGHT walk a button row — with the one
 * seam that RIGHT off a section's Reset steps into the next section rather
 * than stopping. That is the arrangement the overlay already had with one
 * button row, kept rather than grown into a 2-D grid: this is a developer tool
 * with six controls, and a linear list is predictable to walk blind. Every handler consumes its event so nothing
 * leaks to the screen behind the overlay, and each also swallows its `keyup`
 * for the same reason `UnityDialogController` does — a screen with its own
 * keyup handler (FullScreenPlayback's skip/hold-release) would otherwise see
 * the release of a key whose press was consumed here.
 *
 * NAV_OK is never delivered while focus sits in either text field:
 * `NavigationEngine` stops routing non-structural actions to a text-entry
 * target, so the browser gets the key instead. That is why Apply is a separate
 * focusable button — the same arrangement `SignInController` uses for its
 * credential fields.
 */
export class AppVersionOverlayController extends ModalController {
  private readonly mDispatch: UnityAppDispatch;
  private readonly mGetState: () => UnityRootState;

  public constructor(
    dispatch: UnityAppDispatch,
    getState: () => UnityRootState
  ) {
    super(APP_VERSION_OVERLAY_MODAL_NAME);
    this.mDispatch = dispatch;
    this.mGetState = getState;
  }

  protected initialize(): void {
    this.registerHandler(InputAction.NAV_DOWN, (): boolean => {
      this.mDispatch(focusNextControl());
      return true;
    });

    this.registerHandler(InputAction.NAV_RIGHT, (): boolean => {
      this.mDispatch(focusNextControl());
      return true;
    });

    this.registerHandler(InputAction.NAV_UP, (): boolean => {
      this.mDispatch(focusPrevControl());
      return true;
    });

    this.registerHandler(InputAction.NAV_LEFT, (): boolean => {
      this.mDispatch(focusPrevControl());
      return true;
    });

    // Each override owns its own pair, so OK acts on exactly the section the
    // developer is standing in and never touches the other one.
    this.registerHandler(InputAction.NAV_OK, (): boolean => {
      const focusedIndex: number = selectAppVersionOverrideFocusedIndex(
        this.mGetState()
      );
      if (focusedIndex === AppVersionOverrideFocus.VERSION_APPLY) {
        this.mDispatch(applyVersionOverride());
      } else if (focusedIndex === AppVersionOverrideFocus.VERSION_RESET) {
        this.mDispatch(resetVersionOverride());
      } else if (focusedIndex === AppVersionOverrideFocus.MOCK_HOST_APPLY) {
        this.mDispatch(applyMockHostOverride());
      } else if (focusedIndex === AppVersionOverrideFocus.MOCK_HOST_RESET) {
        this.mDispatch(resetMockHostOverride());
      }
      return true;
    });

    // BACK is a pure cancel: the overlay closes, neither the cookie nor the
    // stored mock host is touched, and the app keeps running exactly as it
    // launched.
    this.registerHandler(InputAction.BACK, (): boolean => {
      this.mDispatch(closeAppVersionOverride());
      return true;
    });

    this.registerHandler(InputAction.NAV_UP, (): boolean => true, "keyup");
    this.registerHandler(InputAction.NAV_DOWN, (): boolean => true, "keyup");
    this.registerHandler(InputAction.NAV_LEFT, (): boolean => true, "keyup");
    this.registerHandler(InputAction.NAV_RIGHT, (): boolean => true, "keyup");
    this.registerHandler(InputAction.NAV_OK, (): boolean => true, "keyup");
    this.registerHandler(InputAction.BACK, (): boolean => true, "keyup");
  }
}
